import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import {
  IAppPluginWidget,
  ModelService,
  PluginInjectorWidget,
  PluginInjectorService,
} from "core-client/api";
import {
  BehaviorSubject,
  concatMap,
  distinctUntilChanged,
  filter,
  from,
  Observable,
  of,
  Subject,
  takeUntil,
} from "rxjs";
import { INotification, NgsildConnectorService } from "ngsild-connector";
import { WmsChartService } from "wms-chart-loader";
import { LeafletService } from "../lib/leaflet/leaflet.service";
import {
  ILeafletInstance,
  ITrackingRoute,
} from "../lib/leaflet/leaflet.interface";
import { SharedModule } from "../../shared/shared.module";
import { IExtendedAppPluginWidget } from "../../shared/interfaces/extended-app-plugin-widget.interface";
import { MapWidgetPermissionsService } from "../../shared/services/helpers/map-widget-permissions.service";
import { IMapWidgetPermissions } from "../../shared/interfaces/map-widget-permission.interface";
import { OpenStreetMap } from "../lib/leaflet/providers/open-street-map";
import { WmsChartServer } from "../lib/leaflet/providers/wms-chart-server";
import { OpenSeaMap } from "../lib/leaflet/providers/open-sea-map";
import { WidgetSettingsObserveService } from "../../shared/services/helpers/widget-settings-observe.service";
import { IMapWidgetFormGroupSettings } from "../map-widget-settings/map-widget-settings.interface";
import { UtilsService } from "../../shared/services/helpers/utils.service";
import { INgsiMapBaseEntity } from "../lib/ngsild/interfaces/ngsi-entity.interface";
import { CommonMapHelperService } from "../lib/leaflet/helper/common-map-helper.service";
import * as NgsildConstants from "../lib/ngsild/helpers/ngsild.constants";
import { DataAdapter } from "../lib/ngsild/data-adapter";
import { MapSidepanelContentComponent } from "./map-sidepanel-content/map-sidepanel-content.component";
import { INgsiAIS } from "../lib/ngsild/interfaces/ngsi-ais.interface";
import * as NgsiPlannedPath from "../lib/ngsild/models/ngsi-planned-path";
import { MapToolbarSettingsComponent } from "./map-toolbar-settings/map-toolbar-settings.component";
import { EntityBatchingService } from "../lib/leaflet/entity-batching.service";

@Component({
  selector: "lib-map-widget-view",
  imports: [SharedModule, MapSidepanelContentComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./map-widget-view.component.html",
  styleUrl: "./map-widget-view.component.scss",
})
export class MapWidgetViewComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // Services - Dependency Injection mit inject()
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly viewContainerRef: ViewContainerRef =
    inject(ViewContainerRef);
  private readonly ngsildConnectorService: NgsildConnectorService = inject(
    NgsildConnectorService,
  );
  private readonly pluginInjectorService: PluginInjectorService = inject(
    PluginInjectorService,
  );
  private readonly modelService: ModelService = inject(ModelService);
  private readonly wmsChartService: WmsChartService = inject(WmsChartService);
  protected leafletService: LeafletService = inject(LeafletService);
  protected widgetSettingObserve: WidgetSettingsObserveService = inject(
    WidgetSettingsObserveService,
  );
  protected dataAdapter: DataAdapter = inject(DataAdapter);
  private readonly entityBatchingService: EntityBatchingService = inject(
    EntityBatchingService,
  );
  private readonly ngZone: NgZone = inject(NgZone);

  @HostListener("window:beforeunload")
  onBeforeReload(): void {
    this.ngOnDestroy();
  }

  @ViewChild("mapContainer", { static: false })
  protected mapContainer: ElementRef<HTMLElement> | undefined;

  @Input()
  protected data: PluginInjectorWidget.IWidget | undefined;

  @Input()
  protected isHeaderHovered: boolean = false;

  protected toolbarSettingsContainerRef:
    | ComponentRef<MapToolbarSettingsComponent>
    | undefined;

  // Variables
  protected widgetPermissions: IMapWidgetPermissions =
    MapWidgetPermissionsService.widgetPermissions; // Default Permissions

  private resizeObserver: ResizeObserver | undefined;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();

  protected appPluginWidget: IAppPluginWidget | undefined;
  protected leafletInstance: ILeafletInstance | undefined;

  protected currDashboardId: string | undefined;

  // Timeouts
  private loadTimeoutIds: (number | ReturnType<typeof setTimeout>)[] = []; // Array to track timeout IDs

  // sidePanel for vessels
  protected isVesselSidePanelActive: boolean = false;
  private readonly vesselSubject$: BehaviorSubject<INgsiAIS[]> =
    new BehaviorSubject<INgsiAIS[]>([]);
  protected vesselItems$: Observable<INgsiAIS[]> =
    this.vesselSubject$.asObservable();

  private realtimeEntityQueue = new Map<
    string,
    {
      entity: INgsiMapBaseEntity;
      widget: IAppPluginWidget;
      instance: ILeafletInstance;
    }
  >();
  private realtimeScheduled = false;
  private readonly BATCH_INTERVAL_MS = 250; // Batch every 250ms instead of every frame

  async ngOnInit(): Promise<void> {
    this.appPluginWidget = this.data?.appPluginWidget;
    const widgetId = this.data?.appPluginWidget?._id;
    const user = this.modelService._user.getUser();

    if (!user?._id) {
      console.error(`[MAP Widget] User not found.`);
      return;
    }

    if (!widgetId) {
      console.error(`[MAP Widget] Required widget ID missing.`);
      return;
    }

    const appPluginWidget =
      await this.modelService._appPluginWidget.getById(widgetId);
    if (
      "settings" in appPluginWidget &&
      appPluginWidget?.settings &&
      this.appPluginWidget?.settings
    ) {
      this.appPluginWidget.settings = appPluginWidget.settings;
    }

    this.currDashboardId = user.active_dashboard;
  }

  async ngAfterViewInit(): Promise<void> {
    await this.loadWidgetPermissions();

    const appPluginWidget = this.appPluginWidget;
    if (appPluginWidget) {
      await this.initializeMap(appPluginWidget);
      const instance = this.leafletInstance;

      // initialize resizeObserver
      this.resizeObserver = new ResizeObserver(() => {
        this.leafletService.resizeMap(this.leafletInstance);
        this.adjustControlPosition();
      });

      // observe changes to the map container's size
      if (this.mapContainer?.nativeElement) {
        this.resizeObserver.observe(this.mapContainer.nativeElement);
      }

      this.cdr.detectChanges();

      this.addVesselSidePanel(appPluginWidget, instance);

      if (UtilsService.hasRegisteredNgsiEntities(appPluginWidget)) {
        await this.updateMapBaseOnSettings(appPluginWidget, instance);

        requestAnimationFrame(async (): Promise<void> => {
          await this.queryStoredMapEntities(
            appPluginWidget,
            false,
            true,
            true,
            instance,
          );
        });
      }
    }

    // observe map setting changes
    this.observeSettingChanges();

    // observe ngsild-proxy socketIo connection
    if (this.widgetPermissions.ngsildConnectorEnabled) {
      this.listenOnNgsildServiceEvents();
    }

    this.listenOnIdleService(appPluginWidget);
  }

  ngOnDestroy(): void {
    // Clear to all component subscriptions
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Clear generated hues
    CommonMapHelperService.clearGeneratedHues();

    const appPluginWidget = this.appPluginWidget;
    const leafletInstance = this.leafletInstance;

    // Clear all timeouts
    this.clearOpenTimeouts(leafletInstance);

    if (leafletInstance) {
      leafletInstance.entities.clear();
      leafletInstance.additionalEntities.clear();
      if (appPluginWidget) {
        this.leafletService.clearTemporaryEntities(
          appPluginWidget._id,
          leafletInstance,
        );
        this.dataAdapter.clearInstanceFromInheritedServices(
          appPluginWidget._id,
        );

        // Clear existing layers, markers, popups etc.
        this.leafletService.clearMap(
          false,
          appPluginWidget._id,
          leafletInstance,
        );
        this.leafletService.destroyMap(appPluginWidget._id, leafletInstance);
      }
    }

    // Clear vessel BehaviorSubject
    this.vesselSubject$.complete();

    // Remove HostListener
    window.removeEventListener("window:beforeunload", this.onBeforeReload);

    // Clear extern plugins subscriptions
    if (this.appPluginWidget?._id) {
      // NGSILD Connector cleanup (dashboard-aware)
      this.ngsildConnectorService.unregisterGeoQHandler(
        this.appPluginWidget._id,
        <string>this.appPluginWidget?.class,
      );
      this.ngsildConnectorService.unregisterNotificationHandler(
        this.appPluginWidget._id,
        <string>this.appPluginWidget?.class,
      );

      // WMS Chart Loader cleanup (global token - just unregister widget)
      this.wmsChartService.unregisterNotificationHandler(
        this.appPluginWidget._id,
        <string>this.appPluginWidget?.class,
      );

      this.entityBatchingService.cleanupInstance(this.appPluginWidget._id);
    }

    this.pluginInjectorService.idleService.stopIdleDetection$();
  }

  private async loadWidgetPermissions(): Promise<void> {
    if (this.data) {
      this.widgetPermissions =
        await MapWidgetPermissionsService.getUserAccessRights(
          this.modelService._user.getUser(),
          this.pluginInjectorService,
          this.data,
        );
    }

    WmsChartServer.wmsLayerEnabled =
      this.widgetPermissions.wmsChartLoaderEnabled;
  }

  adjustControlPosition(): void {
    const leafletControl = this.mapContainer?.nativeElement.querySelector(
      ".leaflet-top.leaflet-right",
    );
    if (leafletControl) {
      if (this.isHeaderHovered) {
        this.renderer.setStyle(leafletControl, "top", "35px");
      } else {
        this.renderer.setStyle(leafletControl, "top", "0");
      }
    }

    const toolbarSettingsMenu =
      this.mapContainer?.nativeElement.querySelector(".leaflet-menu");
    if (toolbarSettingsMenu) {
      if (this.isHeaderHovered) {
        this.renderer.setStyle(toolbarSettingsMenu, "top", "35px");
      } else {
        this.renderer.setStyle(toolbarSettingsMenu, "top", "0");
      }
    }

    const navigationsInstrumentWrapper =
      this.mapContainer?.nativeElement.querySelector(
        ".obc-navigations-instrument-leaflet-container",
      );
    if (navigationsInstrumentWrapper) {
      if (this.isHeaderHovered) {
        this.renderer.setStyle(
          navigationsInstrumentWrapper,
          "margin-top",
          "35px",
        );
      } else {
        this.renderer.setStyle(navigationsInstrumentWrapper, "margin-top", "0");
      }
    }
  }

  private async initializeMap(
    appPluginWidget: IAppPluginWidget,
  ): Promise<void> {
    this.leafletService.createInstance(appPluginWidget._id);
    this.leafletInstance = this.leafletService.getInstance(appPluginWidget._id);

    this.leafletService.initMap(this.leafletInstance);
    await this.activateMapTileLayer(appPluginWidget);
  }

  private async activateMapTileLayer(
    appPluginWidget: IAppPluginWidget,
  ): Promise<void> {
    if (!this.currDashboardId) {
      console.warn(
        "[MAP Widget] No dashboard ID found. Cannot activate map tile layer.",
      );
      return;
    }
    const mapId = appPluginWidget._id;
    const baseTileLayer = appPluginWidget.settings["baseTileLayer"] as
      | string
      | undefined;
    const instance = this.leafletInstance;

    if (!baseTileLayer) {
      OpenStreetMap.addOSMBaseLayer(mapId, instance);
      this.leafletService.activateLayer("OSM", instance);
      OpenStreetMap.addMiniMapLayerToInstance(mapId, instance);
      return;
    }

    const { addWmsTilerLayer, wmsLayerEnabled } =
      await WmsChartServer.getFlagsAndSetToken(
        appPluginWidget,
        this.currDashboardId,
        this.wmsChartService,
        baseTileLayer,
        instance,
      );

    this.addSelectedTileLayer(
      baseTileLayer,
      mapId,
      wmsLayerEnabled,
      addWmsTilerLayer,
      instance,
    );

    this.addMiniMap(mapId, wmsLayerEnabled, baseTileLayer, instance);
  }

  private addSelectedTileLayer(
    baseTileLayer: string,
    mapId: string,
    wmsLayerEnabled: boolean,
    addWmsTilerLayer: boolean,
    leafletInstance?: ILeafletInstance,
  ): void {
    // Always add OSM and OpenSeaMap layers
    if (!leafletInstance?.tileLayers["OSM"]?.layer) {
      OpenStreetMap.addOSMBaseLayer(mapId, leafletInstance);
    }

    if (!leafletInstance?.tileLayers["OpenSeaMap"]?.layer) {
      OpenSeaMap.addSeaMapLayer(mapId, leafletInstance);
    }

    if (wmsLayerEnabled && addWmsTilerLayer) {
      WmsChartServer.addWmsLayer(
        mapId,
        <string>WmsChartServer.wmsLayerToken,
        this.wmsChartService,
        leafletInstance,
      );
    }

    const osmTileLayer = leafletInstance?.tileLayers["OSM"]?.layer;
    const openSeaMapTileLayer =
      leafletInstance?.tileLayers["OpenSeaMap"]?.layer;
    const wmsTileLayer = leafletInstance?.tileLayers["WMS-Chart"]?.layer;

    // BaseTileLayer: OpenSeaMap
    if (baseTileLayer === "OpenSeaMap" && openSeaMapTileLayer && osmTileLayer) {
      this.leafletService.activateLayer("OSM", leafletInstance);
      this.leafletService.activateLayer("OpenSeaMap", leafletInstance);
    }
    // BaseTileLayer: OSM
    else if (baseTileLayer === "OSM" && osmTileLayer) {
      this.leafletService.activateLayer("OSM", leafletInstance);
    }
    // BaseTileLayer: WMS-Layer
    else if (baseTileLayer === "WMS-Chart" && wmsTileLayer && wmsLayerEnabled) {
      this.leafletService.activateLayer("WMS-Chart", leafletInstance);
    }
  }

  private addMiniMap(
    mapId: string,
    wmsLayerEnabled: boolean,
    baseTileLayer?: string,
    leafletInstance?: ILeafletInstance,
  ): void {
    // BaseTileLayer: OSM
    if (!baseTileLayer || baseTileLayer === "OSM") {
      OpenStreetMap.addMiniMapLayerToInstance(mapId, leafletInstance);
    }
    // BaseTileLayer: OpenSeaMap
    else if (baseTileLayer === "OpenSeaMap") {
      OpenSeaMap.addMiniMapLayerToInstance(mapId, leafletInstance);
    }
    // BaseTileLayer: WMS-Layer
    else if (baseTileLayer === "WMS-Chart" && wmsLayerEnabled) {
      WmsChartServer.addMiniMapLayerToInstance(mapId, leafletInstance);
    }
  }

  listenOnNgsildServiceEvents(): void {
    if (!this.currDashboardId) {
      console.warn(
        "[MAP Widget] No dashboard ID found. Cannot listen on ngsild-proxy socketIo connection.",
      );
      return;
    }

    this.listenOnBaseNotifications(this.currDashboardId);
    this.listenOnGeoNotifications(this.currDashboardId);

    // Listen if the plugin is switch from deactivate to activate and widget is on dashboard visible
    this.ngsildConnectorService.socket.on("connect", () => {
      if (
        this.ngsildConnectorService.socket.ioSocket.connected &&
        this.widgetPermissions.ngsildConnectorEnabled &&
        this.currDashboardId
      ) {
        this.listenOnBaseNotifications(this.currDashboardId);
        this.listenOnGeoNotifications(this.currDashboardId);
      }
    });
  }

  listenOnIdleService(appPluginWidget?: IAppPluginWidget): void {
    this.pluginInjectorService.idleService
      .checkIfIdle$()
      .pipe(
        distinctUntilChanged(),
        filter(
          (idle: boolean) => this.appPluginWidget?._id === appPluginWidget?._id,
        ),
        takeUntil(this.ngUnsubscribe$),
      )
      .subscribe(async (idle: boolean) => {
        const instance = this.leafletInstance;
        if (instance && idle) {
          instance.trackingRoutes.forEach((route: ITrackingRoute) =>
            route.line.remove(),
          );
          instance.trackingRoutes.clear();
        }

        if (instance && !idle && this.appPluginWidget?._id) {
          await this.queryStoredMapEntities(
            this.appPluginWidget,
            false,
            false,
            false,
            instance,
          );
        }
      });
  }

  private observeSettingChanges(): void {
    // RXJS Observable
    this.widgetSettingObserve.observeWidgetSettings$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (appPluginWidget: IAppPluginWidget) => {
        if (appPluginWidget._id === this.data?.appPluginWidget._id) {
          this.appPluginWidget = appPluginWidget;
          if (this.widgetPermissions.ngsildConnectorEnabled) {
            const instance = this.leafletInstance;
            this.leafletService.clearMap(false, appPluginWidget._id, instance);

            const settings = <IMapWidgetFormGroupSettings>(
              (<unknown>appPluginWidget.settings)
            );
            this.leafletService.removeLayerControl(
              appPluginWidget._id,
              instance,
            );
            this.leafletService.removeUnusedTileLayers(
              settings.baseTileLayer,
              instance,
            );
            this.clearVesselSidePanel();

            await this.updateMapBaseOnSettings(appPluginWidget, instance);

            this.addVesselSidePanel(appPluginWidget, instance);

            await this.activateMapTileLayer(appPluginWidget);
            await this.queryStoredMapEntities(
              appPluginWidget,
              true,
              true,
              true,
              instance,
            );
          }
        }
      });
  }

  async queryStoredMapEntities(
    appPluginWidget: IAppPluginWidget,
    storeChanges: boolean,
    centerView: boolean,
    executeQuery: boolean,
    leafletInstance?: ILeafletInstance,
  ): Promise<void> {
    const settings = <IMapWidgetFormGroupSettings>(
      (<unknown>appPluginWidget.settings)
    );
    if (!leafletInstance || !settings?.entities) return;

    const args = await UtilsService.generateQueryString(
      settings.entities,
      this.ngsildConnectorService,
    );

    const plannedPathNeeded =
      settings.entities.length === 1 &&
      settings.enablePlannedPath &&
      this.widgetPermissions.plannedPathDataEnabled;

    if (!executeQuery) return;

    // Haupt-Query als Observable-Kette
    from([args])
      .pipe(
        filter(() => this.widgetPermissions.ngsildConnectorEnabled),
        concatMap((queryArgs) =>
          this.ngsildConnectorService.queryEntitiesById$(queryArgs).pipe(
            takeUntil(this.ngUnsubscribe$),
            concatMap((result) => {
              const instance = this.leafletService.getInstance(
                appPluginWidget._id,
              );
              if (!instance) return of(null);

              if (NgsildConstants.emptyArray(result)) {
                this.noDataMap(instance);
                return of(null);
              }

              if (storeChanges) {
                this.widgetSettingObserve.updateWidgetSettingsBasedOnNotification(
                  appPluginWidget as IExtendedAppPluginWidget,
                  result[0] as INgsiMapBaseEntity,
                  this.modelService,
                );
              }

              if (NgsildConstants.isSingleEntity(result)) {
                const entity = result[0] as INgsiMapBaseEntity;
                this.addDataToMap(
                  appPluginWidget._id,
                  entity,
                  settings,
                  instance,
                );

                if (centerView) {
                  const timeoutId = setTimeout(() => {
                    this.leafletService.viewOnMarkerPosition(
                      appPluginWidget._id,
                      entity,
                      instance,
                    );
                  }, 500);
                  instance.timeoutIds.push(timeoutId);
                }
              } else if (NgsildConstants.hasMultipleEntities(result)) {
                const entities = result as INgsiMapBaseEntity[];
                entities.forEach((entity) => {
                  if (NgsildConstants.entityFulfillsClaim(entity)) {
                    this.addDataToMap(
                      appPluginWidget._id,
                      entity,
                      settings,
                      instance,
                    );
                  }
                });

                if (centerView) {
                  const timeoutId = setTimeout(() => {
                    this.leafletService.mapFitBounds(
                      appPluginWidget._id,
                      entities[0].type,
                      true,
                      settings.entities,
                      instance,
                    );
                  }, 500);
                  instance.timeoutIds.push(timeoutId);
                }
              }

              return of(result);
            }),
          ),
        ),
        // Wenn geplant, danach PlannedPath abfragen
        concatMap((result) => {
          if (!plannedPathNeeded || !result) return of(null);

          const plannedPathArgs = { ...args };
          const splitIdPattern = plannedPathArgs.idPattern.split(":");
          splitIdPattern[2] = NgsiPlannedPath.plannedPathType;
          plannedPathArgs.idPattern = splitIdPattern.join(":");

          return this.ngsildConnectorService
            .queryEntitiesById$(plannedPathArgs)
            .pipe(
              takeUntil(this.ngUnsubscribe$),
              filter((result) => !!result.length),
              concatMap((result) => {
                const instance = this.leafletService.getInstance(
                  appPluginWidget._id,
                );
                if (!instance) return of(null);

                const entity = result[0] as INgsiMapBaseEntity;
                if (
                  NgsildConstants.isSingleEntity(result) &&
                  NgsildConstants.entityFulfillsClaim(entity) &&
                  NgsildConstants.isDateObservedToday(entity)
                ) {
                  this.addDataToMap(
                    appPluginWidget._id,
                    entity,
                    settings,
                    instance,
                  );
                }

                return of(null);
              }),
            );
        }),
      )
      .subscribe();
  }

  private listenOnBaseNotifications(currDashboardId: string): void {
    this.ngsildConnectorService.registerNotificationHandler(
      <string>this.appPluginWidget?._id,
      currDashboardId,
      <string>this.appPluginWidget?.class,
      (notification: INotification) => {
        const isRelevant = NgsildConstants.filterIncomingNotifications(
          notification,
          this.widgetPermissions,
          <IMapWidgetFormGroupSettings>(
            (<unknown>this.appPluginWidget?.settings)
          ),
        );

        if (!isRelevant) {
          return;
        }

        const appPluginWidget = this.appPluginWidget;
        if (!appPluginWidget) {
          return;
        }

        const instance = this.leafletService.getInstance(appPluginWidget._id);
        if (!instance) {
          return;
        }

        const settings = <IMapWidgetFormGroupSettings>(
          (<unknown>appPluginWidget.settings)
        );
        const entity = <INgsiMapBaseEntity>notification.data[0];

        if (NgsildConstants.entityFulfillsClaim(entity)) {
          const mandatoryEntity = NgsildConstants.appPluginWidgetHasEntityId(
            settings.entities,
            entity,
          );

          const addToMap = this.leafletService.isVisibleOnMap(entity, instance);
          if (!addToMap && !mandatoryEntity) {
            return;
          }

          this.entityBatchingService.processEntity(
            entity,
            notification,
            appPluginWidget,
            instance,
            (widget, notif, inst) =>
              this.handleNotification(widget, notif, inst),
          );
        }
      },
    );
  }

  private listenOnGeoNotifications(currDashboardId: string) {
    this.ngsildConnectorService.registerGeoQHandler(
      <string>this.appPluginWidget?._id,
      currDashboardId,
      <string>this.appPluginWidget?.class,
      (notification: INotification) => {
        const isRelevant = NgsildConstants.filterIncomingGeoQNotifications(
          notification,
          this.widgetPermissions,
          <IMapWidgetFormGroupSettings>(
            (<unknown>this.appPluginWidget?.settings)
          ),
        );

        if (!isRelevant) {
          return;
        }

        const appPluginWidget = this.appPluginWidget;
        if (!Array.isArray(appPluginWidget?.settings?.["entities"])) {
          return;
        }

        const instance = this.leafletService.getInstance(appPluginWidget._id);
        if (!instance) {
          return;
        }

        const entity = <INgsiMapBaseEntity>notification.data[0];
        if (NgsildConstants.entityFulfillsClaim(entity)) {
          const addToMap = this.leafletService.isVisibleOnMap(entity, instance);
          if (!addToMap) {
            return;
          }

          this.handleGeoQNotification(appPluginWidget, notification, instance);
        }
      },
    );
  }

  private handleNotification(
    appPluginWidget: IAppPluginWidget,
    notification: INotification,
    instance: ILeafletInstance,
  ) {
    const settings = <IMapWidgetFormGroupSettings>(
      (<unknown>appPluginWidget.settings)
    );
    const entity = <INgsiMapBaseEntity>notification.data[0];

    if (!NgsildConstants.isSingleEntity(notification.data)) {
      return;
    }

    const entityId = entity.id;
    this.realtimeEntityQueue.set(entityId, {
      entity,
      widget: appPluginWidget,
      instance,
    });

    if (!this.realtimeScheduled) {
      this.realtimeScheduled = true;

      // Use setTimeout with fixed interval instead of requestAnimationFrame
      // This allows token refresh timers to fire between batches
      setTimeout(
        () => this.flushRealtimeEntityQueue(settings),
        this.BATCH_INTERVAL_MS,
      );
    }
  }

  private flushRealtimeEntityQueue(
    settings: IMapWidgetFormGroupSettings,
  ): void {
    // Process queue OUTSIDE Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      this.realtimeEntityQueue.forEach(({ entity, widget, instance }) => {
        this.addDataToMap(widget._id, entity, settings, instance);

        if (
          this.leafletService.shouldCenterMapOnMarkers(instance, settings) &&
          NgsildConstants.isEntityInMapFocus(settings, entity)
        ) {
          if (NgsildConstants.hasMultipleEntities(settings.entities)) {
            this.leafletService.mapFitBounds(
              widget._id,
              entity.type,
              false,
              settings.entities,
              instance,
            );
          } else {
            this.leafletService.viewOnMarkerPosition(
              widget._id,
              entity,
              instance,
            );
          }
        }
      });

      this.realtimeEntityQueue.clear();
      this.realtimeScheduled = false;
    });
  }

  private handleGeoQNotification(
    appPluginWidget: IAppPluginWidget,
    notification: INotification,
    instance: ILeafletInstance,
  ) {
    const settings = <IMapWidgetFormGroupSettings>(
      (<unknown>appPluginWidget.settings)
    );
    const entity = <INgsiMapBaseEntity>notification.data[0];

    const isTemporary = true;
    entity["shipType"] = 100;

    this.addDataToMap(
      appPluginWidget._id,
      entity,
      settings,
      instance,
      isTemporary,
    );
  }

  async updateMapBaseOnSettings(
    appPluginWidget: IAppPluginWidget,
    instance?: ILeafletInstance,
  ): Promise<void> {
    const settings = <IMapWidgetFormGroupSettings>(
      (<unknown>appPluginWidget.settings)
    );

    if (instance && settings?.entities) {
      instance.centerOnMarkerActive = settings.centerMapOnMarkers;
      this.leafletService.updateZoom(settings.zoom, instance);

      instance.dataConfig.dataModel.AIS.showTempLayer =
        settings.enableAisTargets;
      instance.dataConfig.dataModel.MaritimeTargets.showDefaultLayer =
        settings.enableMaritimeTargets;
      instance.dataConfig.dataModel.PlannedPath.showDefaultLayer =
        settings.enablePlannedPath;
      instance.dataConfig.dataModel.RadarTarget.showDefaultLayer =
        settings.enableRadarTarget;
      instance.dataConfig.dataModel.FusionMaritimeTarget.showDefaultLayer =
        settings.enableFusionMaritimeTarget;

      this.leafletService.updateMapDataConfig(
        appPluginWidget._id,
        instance.dataConfig,
      );

      this.loadMapInteractionControls(appPluginWidget, instance);
    }
  }

  loadMapInteractionControls(
    appPluginWidget: IAppPluginWidget,
    instance: ILeafletInstance,
  ) {
    this.leafletService.addLayerControl(appPluginWidget._id, instance);
    this.leafletService.addMousePositionControl(appPluginWidget._id, instance);

    this.loadLeafletToolbarItems(appPluginWidget, instance);
  }

  loadLeafletToolbarItems(
    appPluginWidget: IAppPluginWidget,
    instance: ILeafletInstance,
  ) {
    this.leafletService.addSettingsControl(
      appPluginWidget._id,
      this.viewContainerRef,
      appPluginWidget.settings,
      this.widgetPermissions,
      this.toolbarSettingsContainerRef,
      instance,
    );

    this.leafletService.addCustomToolbarGroup(
      appPluginWidget._id,
      instance,
      appPluginWidget.settings,
    );

    this.leafletService.addMeasurementControl(appPluginWidget._id, instance);
  }

  private addDataToMap(
    appPluginWidgetId: string,
    entity: INgsiMapBaseEntity,
    settings: IMapWidgetFormGroupSettings,
    instance: ILeafletInstance,
    temporaryData?: boolean,
  ): void {
    this.ngZone.runOutsideAngular(() => {
      this.dataAdapter.processIncomingData(
        appPluginWidgetId,
        entity,
        settings,
        instance,
        temporaryData,
      );

      const isVessel = entity.type === "AIS";

      const isTemporaryEntity =
        temporaryData ||
        !NgsildConstants.appPluginWidgetHasEntityId(
          settings.entities,
          <INgsiAIS>entity,
        );

      if (isVessel && !isTemporaryEntity) {
        this.updateVesselItems(<INgsiAIS>entity);
      }
    });
  }

  private noDataMap(instance?: ILeafletInstance): void {
    const appPluginWidget = this.appPluginWidget;
    if (instance) {
      this.leafletService.clearMap(true, appPluginWidget?._id, instance);
      this.leafletService.updateLatLng(
        instance.latitude,
        instance.longitude,
        instance,
      );
      this.leafletService.noDataFound("AIS", instance);
    }
  }

  private clearOpenTimeouts(instance?: ILeafletInstance): void {
    if (instance) {
      instance.timeoutIds.forEach(
        (timeoutId: number | ReturnType<typeof setTimeout>) =>
          clearTimeout(timeoutId),
      );
      instance.timeoutIds = [];
    }
    this.loadTimeoutIds.forEach(
      (timeoutId: number | ReturnType<typeof setTimeout>) =>
        clearTimeout(timeoutId),
    );
    this.loadTimeoutIds = [];
  }

  private addVesselSidePanel(
    appPluginWidget: IAppPluginWidget,
    instance?: ILeafletInstance,
  ): void {
    // Extra initialisierung für Vessel SidePanel
    if (appPluginWidget.settings?.["dataSource"] === "MMSI") {
      this.isVesselSidePanelActive = true;
      this.cdr.detectChanges(); // Trigger change detection to ensure DOM is updated
    }

    if (this.isVesselSidePanelActive) {
      this.leafletService.addVesselOverviewSidePanel(
        appPluginWidget._id,
        instance,
      );
    }
  }

  private updateVesselItems(nextVessel: INgsiAIS): void {
    const currentVessels: INgsiAIS[] = this.vesselSubject$.value;

    // Find if the vessel already exists in the array
    const vesselIndex = currentVessels.findIndex(
      (vessel) => vessel.id === nextVessel.id,
    );

    let updatedVessels: INgsiAIS[];
    if (vesselIndex === -1) {
      // If the team does not exist, add it
      updatedVessels = [...currentVessels, nextVessel];
    } else {
      // If the team exists, update it
      updatedVessels = [
        ...currentVessels.slice(0, vesselIndex),
        nextVessel,
        ...currentVessels.slice(vesselIndex + 1),
      ];
    }

    // Emit the updated array
    this.vesselSubject$.next(updatedVessels);
    this.cdr.markForCheck(); // Trigger change detection for OnPush strategy
  }

  private clearVesselSidePanel(): void {
    this.vesselSubject$.next([]);
  }
}
